import { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Users, MagnifyingGlass, X, Plus, ArrowsLeftRight, TrendUp, Package,
  CurrencyDollar, Cube, Star, ArrowsClockwise, Ticket, CheckCircle,
  Spinner, CaretUp, CaretDown, Minus
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, RadarChart,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, Cell
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#7d64ff', '#f6a609', '#28cd8c', '#ea4b5a'];
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);
const fmtNum = (n) => new Intl.NumberFormat('en-IN').format(n || 0);

function SellerSearchInput({ onSelect, excludeIds, color, index }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doSearch = useCallback((val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    if (!val || val.length < 2) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/admin/sellers?search=${encodeURIComponent(val)}&limit=10`, { withCredentials: true });
        const list = (res.data.sellers || []).filter(s => s.role === 'seller' && !excludeIds.includes(s.user_id));
        setResults(list);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
  }, [excludeIds]);

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9c9db7]" size={15} />
        <Input
          value={search}
          onChange={e => doSearch(e.target.value)}
          placeholder="Search seller by name or email..."
          className="pl-9 pr-3 rounded-lg text-sm"
          onFocus={() => results.length > 0 && setOpen(true)}
          data-testid={`compare-search-${index}`}
        />
        {loading && <Spinner className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-[#9c9db7]" size={14} />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white rounded-xl border border-[#e5e5ea] shadow-lg max-h-52 overflow-y-auto">
          {results.map(s => (
            <button key={s.user_id}
              onClick={() => { onSelect(s); setSearch(''); setOpen(false); setResults([]); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#f8f7ff] transition-colors text-left"
              data-testid={`compare-option-${s.user_id}`}
            >
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white" style={{ backgroundColor: color }}>
                {(s.name || '?')[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[#0A0A0A] truncate">{s.name}</p>
                <p className="text-[10px] text-[#9c9db7] truncate">{s.email}{s.business_name ? ` - ${s.business_name}` : ''}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function MetricRow({ label, values, format = 'number', higherBetter = true, icon: Icon }) {
  const nums = values.map(v => typeof v === 'number' ? v : 0);
  const best = higherBetter ? Math.max(...nums) : Math.min(...nums);
  const maxVal = Math.max(...nums, 1);

  return (
    <div className="flex items-center gap-4 py-3 border-b border-[#f0f0f5] last:border-0 hover:bg-[#fcfcfe] transition-colors px-4 -mx-4">
      <div className="w-44 flex items-center gap-2 flex-shrink-0">
        {Icon && <Icon size={15} className="text-[#9c9db7]" weight="bold" />}
        <span className="text-xs font-semibold text-[#5a5c87] uppercase tracking-wider">{label}</span>
      </div>
      <div className="flex-1 grid gap-4" style={{ gridTemplateColumns: `repeat(${values.length}, 1fr)` }}>
        {values.map((v, i) => {
          const isBest = nums[i] === best && nums.filter(n => n === best).length === 1;
          const barWidth = (nums[i] / maxVal) * 100;
          return (
            <div key={i} className="relative">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-sm font-bold ${isBest ? 'text-[#0A0A0A]' : 'text-[#5a5c87]'}`}>
                  {format === 'currency' ? fmt(v) : format === 'percent' ? `${v}%` : fmtNum(v)}
                </span>
                {isBest && <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ backgroundColor: `${COLORS[i]}15`, color: COLORS[i] }}>Best</span>}
              </div>
              <div className="h-2 bg-[#f0f0f5] rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${barWidth}%`, backgroundColor: COLORS[i] }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ComparisonRadarChart({ sellers }) {
  if (!sellers.length) return null;
  const metrics = [
    { key: 'fulfillment_rate', label: 'Fulfillment', max: 100 },
    { key: 'performance_score', label: 'Score', max: 100 },
    { key: 'cancellation_rate', label: 'Low Cancellation', max: 100, invert: true },
    { key: 'return_rate', label: 'Low Returns', max: 100, invert: true },
    { key: 'ticket_resolution_rate', label: 'Ticket Resolution', max: 100 },
  ];

  const data = metrics.map(m => {
    const point = { metric: m.label };
    sellers.forEach((s, i) => {
      let val = s.metrics[m.key] || 0;
      if (m.invert) val = Math.max(0, 100 - val);
      point[`seller_${i}`] = val;
    });
    return point;
  });

  return (
    <div className="bg-white border border-[#e5e5ea] rounded-xl p-5">
      <h3 className="text-sm font-bold text-[#0A0A0A] mb-3">Performance Radar</h3>
      <ResponsiveContainer width="100%" height={300}>
        <RadarChart data={data} cx="50%" cy="50%" outerRadius="70%">
          <PolarGrid stroke="#e5e5ea" />
          <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: '#5a5c87' }} />
          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
          {sellers.map((s, i) => (
            <Radar key={s.user_id} name={s.name} dataKey={`seller_${i}`} stroke={COLORS[i]} fill={COLORS[i]} fillOpacity={0.15} strokeWidth={2} />
          ))}
          <Legend iconType="circle" wrapperStyle={{ fontSize: '11px' }} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ComparisonBarChart({ sellers, metricKey, label, format = 'number' }) {
  if (!sellers.length) return null;
  const data = sellers.map((s, i) => ({
    name: s.name.length > 15 ? s.name.slice(0, 15) + '...' : s.name,
    value: s.metrics[metricKey] || 0,
    fill: COLORS[i]
  }));

  return (
    <div className="bg-white border border-[#e5e5ea] rounded-xl p-5">
      <h3 className="text-sm font-bold text-[#0A0A0A] mb-3">{label}</h3>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
          <XAxis type="number" tick={{ fontSize: 10, fill: '#9c9db7' }} tickFormatter={v => format === 'currency' ? `${(v/100000).toFixed(0)}L` : format === 'percent' ? `${v}%` : fmtNum(v)} />
          <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: '#5a5c87' }} />
          <Tooltip formatter={v => format === 'currency' ? fmt(v) : format === 'percent' ? `${v}%` : fmtNum(v)} />
          <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={28}>
            {data.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function SellerComparisonPage() {
  const [searchParams] = useSearchParams();
  const [selectedSellers, setSelectedSellers] = useState([]);
  const [comparisonData, setComparisonData] = useState([]);
  const [loading, setLoading] = useState(false);

  const runComparison = useCallback(async () => {
    if (selectedSellers.length < 2) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/sellers/compare`, {
        seller_ids: selectedSellers.map(s => s.user_id)
      }, { withCredentials: true });
      setComparisonData(res.data.sellers || []);
    } catch (e) {
      toast.error('Comparison failed');
      setComparisonData([]);
    } finally { setLoading(false); }
  }, [selectedSellers]);

  // Pre-load seller IDs from URL params (from AdminSellersPage "Compare" button)
  useEffect(() => {
    const idsParam = searchParams.get('ids');
    if (idsParam) {
      const ids = idsParam.split(',').filter(Boolean).slice(0, 4);
      if (ids.length >= 2) {
        const fetchPreSelected = async () => {
          try {
            const promises = ids.map(id => axios.get(`${API_URL}/api/admin/sellers?search=${id}&limit=5`, { withCredentials: true }));
            const responses = await Promise.all(promises);
            const found = [];
            responses.forEach((res, idx) => {
              const sellers = res.data.sellers || [];
              const match = sellers.find(s => s.user_id === ids[idx]);
              if (match) found.push(match);
            });
            if (found.length >= 2) {
              setSelectedSellers(found);
            }
          } catch {}
        };
        fetchPreSelected();
      }
    }
  }, [searchParams]);

  // Auto-compare when 2+ sellers selected
  useEffect(() => {
    if (selectedSellers.length >= 2) {
      runComparison();
    } else {
      setComparisonData([]);
    }
  }, [selectedSellers, runComparison]);

  const addSeller = (seller) => {
    if (selectedSellers.length >= 4) { toast.error('Maximum 4 sellers'); return; }
    if (selectedSellers.find(s => s.user_id === seller.user_id)) { toast.error('Already selected'); return; }
    setSelectedSellers(prev => [...prev, seller]);
  };

  const removeSeller = (userId) => {
    setSelectedSellers(prev => prev.filter(s => s.user_id !== userId));
  };

  const excludeIds = selectedSellers.map(s => s.user_id);

  return (
    <DashboardLayout title="Seller Comparison" subtitle="Compare up to 4 sellers side-by-side on key metrics">
      {/* Seller Selection Section */}
      <div className="bg-white border border-[#e5e5ea] rounded-xl p-5 mb-6" data-testid="comparison-selector-panel">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <ArrowsLeftRight size={18} className="text-[#7d64ff]" weight="bold" />
            <h2 className="text-sm font-bold text-[#0A0A0A]">Select Sellers to Compare</h2>
            <span className="text-[10px] font-semibold text-[#9c9db7] bg-[#f5f5f7] px-2 py-0.5 rounded-full">{selectedSellers.length}/4</span>
          </div>
          {selectedSellers.length > 0 && (
            <Button variant="ghost" size="sm" className="text-[#ea4b5a] text-xs" onClick={() => setSelectedSellers([])} data-testid="clear-all-sellers">
              <X size={13} className="mr-1" /> Clear All
            </Button>
          )}
        </div>

        {/* Selected Sellers Tags */}
        <div className="flex flex-wrap gap-2 mb-4">
          {selectedSellers.map((s, i) => (
            <div key={s.user_id} className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg border" style={{ borderColor: `${COLORS[i]}40`, backgroundColor: `${COLORS[i]}08` }} data-testid={`selected-seller-${s.user_id}`}>
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ backgroundColor: COLORS[i] }}>
                {(s.name || '?')[0].toUpperCase()}
              </div>
              <span className="text-xs font-semibold" style={{ color: COLORS[i] }}>{s.name}</span>
              <button onClick={() => removeSeller(s.user_id)} className="ml-1 p-0.5 rounded hover:bg-[#ea4b5a]/10 transition-colors" data-testid={`remove-seller-${s.user_id}`}>
                <X size={12} className="text-[#9c9db7] hover:text-[#ea4b5a]" />
              </button>
            </div>
          ))}
          {selectedSellers.length < 4 && (
            <div className="w-64">
              <SellerSearchInput onSelect={addSeller} excludeIds={excludeIds} color={COLORS[selectedSellers.length]} index={selectedSellers.length} />
            </div>
          )}
        </div>

        {selectedSellers.length < 2 && (
          <p className="text-xs text-[#9c9db7] flex items-center gap-1">
            <Plus size={12} /> Select at least 2 sellers to start comparing
          </p>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-16 gap-3 text-[#9c9db7]">
          <Spinner size={20} className="animate-spin" />
          <span className="text-sm font-medium">Computing comparison metrics...</span>
        </div>
      )}

      {/* Comparison Results */}
      {!loading && comparisonData.length >= 2 && (
        <div className="space-y-6" data-testid="comparison-results">
          {/* Seller Header Cards */}
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${comparisonData.length}, 1fr)` }}>
            {comparisonData.map((s, i) => (
              <div key={s.user_id} className="bg-white border-2 rounded-xl p-4 relative overflow-hidden" style={{ borderColor: COLORS[i] }} data-testid={`comparison-card-${s.user_id}`}>
                <div className="absolute top-0 left-0 right-0 h-1" style={{ backgroundColor: COLORS[i] }} />
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: COLORS[i] }}>
                    {(s.name || '?')[0].toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-[#0A0A0A] truncate">{s.name}</p>
                    <p className="text-[10px] text-[#9c9db7] truncate">{s.email}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-[#f8f7ff] rounded-lg p-2 text-center">
                    <p className="text-[10px] text-[#9c9db7] uppercase tracking-wider">Score</p>
                    <p className="text-xl font-black" style={{ color: s.metrics.performance_score >= 60 ? '#28cd8c' : s.metrics.performance_score >= 35 ? '#f6a609' : '#ea4b5a' }}>
                      {s.metrics.performance_score}
                    </p>
                  </div>
                  <div className="bg-[#f8f7ff] rounded-lg p-2 text-center">
                    <p className="text-[10px] text-[#9c9db7] uppercase tracking-wider">Revenue</p>
                    <p className="text-sm font-bold text-[#28cd8c]">{fmt(s.metrics.total_revenue)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Detailed Metrics Table */}
          <div className="bg-white border border-[#e5e5ea] rounded-xl p-5" data-testid="metrics-table">
            <h3 className="text-sm font-bold text-[#0A0A0A] mb-1">Detailed Metrics</h3>
            <p className="text-[11px] text-[#9c9db7] mb-4">Side-by-side comparison of all key performance indicators</p>

            {/* Column Headers */}
            <div className="flex items-center gap-4 pb-3 border-b-2 border-[#e5e5ea] mb-1 px-4 -mx-4">
              <div className="w-44 flex-shrink-0">
                <span className="text-[10px] font-bold text-[#9c9db7] uppercase tracking-wider">Metric</span>
              </div>
              <div className="flex-1 grid gap-4" style={{ gridTemplateColumns: `repeat(${comparisonData.length}, 1fr)` }}>
                {comparisonData.map((s, i) => (
                  <div key={s.user_id} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                    <span className="text-[10px] font-bold text-[#5a5c87] uppercase tracking-wider truncate">{s.name}</span>
                  </div>
                ))}
              </div>
            </div>

            <MetricRow label="Total Orders" icon={Package} values={comparisonData.map(s => s.metrics.total_orders)} />
            <MetricRow label="Delivered" icon={CheckCircle} values={comparisonData.map(s => s.metrics.delivered_orders)} />
            <MetricRow label="Revenue" icon={CurrencyDollar} values={comparisonData.map(s => s.metrics.total_revenue)} format="currency" />
            <MetricRow label="Avg Order Value" icon={CurrencyDollar} values={comparisonData.map(s => s.metrics.avg_order_value)} format="currency" />
            <MetricRow label="Fulfillment Rate" icon={TrendUp} values={comparisonData.map(s => s.metrics.fulfillment_rate)} format="percent" />
            <MetricRow label="Cancellation Rate" icon={CaretDown} values={comparisonData.map(s => s.metrics.cancellation_rate)} format="percent" higherBetter={false} />
            <MetricRow label="Return Rate" icon={ArrowsClockwise} values={comparisonData.map(s => s.metrics.return_rate)} format="percent" higherBetter={false} />
            <MetricRow label="Total Returns" icon={ArrowsClockwise} values={comparisonData.map(s => s.metrics.total_returns)} higherBetter={false} />
            <MetricRow label="Products" icon={Cube} values={comparisonData.map(s => s.metrics.total_products)} />
            <MetricRow label="Active Products" icon={Cube} values={comparisonData.map(s => s.metrics.active_products)} />
            <MetricRow label="Review Rating" icon={Star} values={comparisonData.map(s => s.metrics.review_avg_rating)} />
            <MetricRow label="Open Tickets" icon={Ticket} values={comparisonData.map(s => s.metrics.open_tickets)} higherBetter={false} />
            <MetricRow label="Ticket Resolution" icon={Ticket} values={comparisonData.map(s => s.metrics.ticket_resolution_rate)} format="percent" />
            <MetricRow label="Performance Score" icon={TrendUp} values={comparisonData.map(s => s.metrics.performance_score)} />
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ComparisonRadarChart sellers={comparisonData} />
            <ComparisonBarChart sellers={comparisonData} metricKey="total_revenue" label="Revenue Comparison" format="currency" />
            <ComparisonBarChart sellers={comparisonData} metricKey="total_orders" label="Orders Comparison" />
            <ComparisonBarChart sellers={comparisonData} metricKey="fulfillment_rate" label="Fulfillment Rate %" format="percent" />
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && comparisonData.length === 0 && selectedSellers.length < 2 && (
        <div className="bg-white border border-[#e5e5ea] rounded-xl py-20 flex flex-col items-center justify-center" data-testid="comparison-empty-state">
          <div className="w-16 h-16 rounded-2xl bg-[#7d64ff]/10 flex items-center justify-center mb-4">
            <ArrowsLeftRight size={28} className="text-[#7d64ff]" weight="bold" />
          </div>
          <h3 className="text-base font-bold text-[#0A0A0A] mb-1">Compare Seller Performance</h3>
          <p className="text-xs text-[#9c9db7] max-w-sm text-center">Search and select 2-4 sellers above to see a detailed side-by-side comparison of their revenue, orders, fulfillment, ratings, and more.</p>
        </div>
      )}
    </DashboardLayout>
  );
}
